import * as path from 'path';
import fs from 'fs-extra';

export interface Route {
	path: string;
	name: string;
	component: string;
	params?: string[];
}

// Helper to only write files when content changes to avoid unnecessary HMR triggers
async function writeFileIfChanged(filePath: string, content: string): Promise<boolean> {
	try {
		const exists = await fs.pathExists(filePath);
		if (exists) {
			const existingContent = await fs.readFile(filePath, 'utf-8');
			if (existingContent === content) {
				return false; // No change needed
			}
		}
		await fs.writeFile(filePath, content, 'utf-8');
		return true; // File was written
	} catch (error) {
		// If there's an error reading, just write the file
		await fs.writeFile(filePath, content, 'utf-8');
		return true;
	}
}

export function createTypeScriptPlugin(cwd: string, routes: Route[]) {
	return {
		name: 'frame-typescript',
		async buildStart() {
			await generateRouteTypes(cwd, routes);
			await generateGlobalTypes(cwd);
		},
	};
}

async function generateRouteTypes(cwd: string, routes: Route[]): Promise<void> {
	const frameDir = path.join(cwd, 'src', '.frame');
	await fs.ensureDir(frameDir);

	// Generate route names union type
	const routeNames = routes.map(route => `"${route.name}"`).join(' | ');

	// Generate route params types
	const routeParamsTypes = routes.map(route => {
		if (route.params && route.params.length > 0) {
			const paramsType = route.params.map(param => `${param}: string`).join('; ');
			return `  "${route.name}": { ${paramsType} };`;
		}
		return `  "${route.name}": {};`;
	}).join('\n');

	// Generate route paths union type
	const routePaths = routes.map(route => `"${route.path}"`).join(' | ');

	const typesContent = `// Auto-generated route types - do not edit manually
export type RouteName = ${routeNames || 'never'};

export type RoutePaths = ${routePaths || 'never'};

export interface RouteParams {
${routeParamsTypes}
}

export interface RouteLocationTyped<T extends RouteName = RouteName> {
  name: T;
  params: RouteParams[T];
  path: string;
  query?: Record<string, string | string[]>;
  hash?: string;
}

// Navigation helpers
export interface RouterTyped {
  push<T extends RouteName>(to: { name: T; params?: RouteParams[T]; query?: Record<string, any> }): Promise<void>;
  push(to: string): Promise<void>;
  replace<T extends RouteName>(to: { name: T; params?: RouteParams[T]; query?: Record<string, any> }): Promise<void>;
  replace(to: string): Promise<void>;
  go(delta: number): void;
  back(): void;
  forward(): void;
}

// Route information for each view
export interface RouteInfo {
${routes.map(route => `  "${route.name}": { path: "${route.path}"; component: "${route.component}" };`).join('\n')}
}

declare module 'vue-router' {
  interface Router extends RouterTyped {}
  
  interface RouteLocationNormalized extends RouteLocationTyped {}
  
  interface RouteLocationNormalizedLoaded extends RouteLocationTyped {}
}

declare module '@vue/runtime-core' {
  interface ComponentCustomProperties {
    $route: RouteLocationTyped;
    $router: RouterTyped;
  }
}
`;

	const typesPath = path.join(frameDir, 'types.ts');
	const wasWritten = await writeFileIfChanged(typesPath, typesContent);

	if (wasWritten) {
		console.log('[Frame TypeScript] Route types generated at:', typesPath);
	}
}

async function generateGlobalTypes(cwd: string): Promise<void> {
	const frameDir = path.join(cwd, 'src', '.frame');
	await fs.ensureDir(frameDir);

	const globalTypesContent = `// Auto-generated global Frame types - do not edit manually
import type { RouteName, RouteParams, RouterTyped } from './types';

declare global {
  interface Window {
    __FRAME_DEV__?: boolean;
  }
}

// Auto-import common Frame utilities
declare module '#frame' {
  export type { RouteName, RouteParams, RouterTyped } from './types';
  export { useRouter, useRoute } from 'vue-router';
}

export {};
`;

	const globalTypesPath = path.join(frameDir, 'global.d.ts');
	const wasWritten = await writeFileIfChanged(globalTypesPath, globalTypesContent);

	if (wasWritten) {
		console.log('[Frame TypeScript] Global types generated at:', globalTypesPath);
	}
}

export function extractRouteParams(routePath: string): string[] {
	const paramRegex = /:([^/]+)/g;
	const params: string[] = [];
	let match;

	while ((match = paramRegex.exec(routePath)) !== null) {
		params.push(match[1]);
	}

	return params;
} 